import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { LeftHeadDecoration, DetailRow, TxidLink } from '../../../styles/main/MainCommonStyles'
import { DText, Flex } from '../../../styles/CommonStyles'
import Text from '../../../core/utils/Text'
import { formatDate, formatNumber, isValidURL } from '../../../core/utils/common'
import { TradeTypeBadge } from '../../../styles/main/orders'
import { getTradeTypeColor } from '../../../core/utils/theme'
import { useMainContext } from '../../../core/contexts/main'
import ModalLayout from '../../../components/layout/main/ModalLayout'
import Operation from '../../../components/common/utils/Operation'
import useBankTransactions from '../../../core/hooks/main/bank-transactions/useBankTransactions'
import {
	useGetABankTransaction,
	useGetAccountingImage,
} from '../../../core/services/react-query/report/banking'
import TransactionModal from '../../../components/modals/TransactionModal'
import DetailsLayout from '../../../components/layout/main/DetailsLayout'
import { BASE_URL } from '../../../core/constants/urls'
import { useGetAUser } from '../../../core/services/react-query/user'
import CoinRow from '../../../components/common/common/CoinRow'
import { normalFetch, pureFetch } from '../../../core/services/fetch-api/get'
import { BiLoader } from 'react-icons/bi'

const BankTransactionDetails = () => {
	const params = useParams()
	const { lang } = useMainContext()

	const { data: transaction, isFetching, isError } = useGetABankTransaction(params.detailId)
	const { data: user, refetch } = useGetAUser(transaction?.userId)

	const [imageUrl, setImageUrl] = useState('')
	const [isImageFullScreen, setIsImageFullScreen] = useState(false)

	const toggleFullScreen = () => {
		setIsImageFullScreen((prev) => !prev)
	}
	const [fileType, setFileType] = useState(null)

	useEffect(() => {
		if (!!transaction) {
			const fileUrl = transaction.fields?.find((x) => x.name === 'File')?.value;
			if (!!fileUrl) {
				const fetchFile = async () => {
					try {
						const response = await pureFetch(fileUrl);

						if (response.status !== 200) throw new Error('Failed to fetch file');

						let fileTypeFromUrl = 'image';
						let mimeType = 'image/png';

						if (fileUrl.endsWith('.pdf')) {
							fileTypeFromUrl = 'pdf';
							mimeType = 'application/pdf';
						}

						setFileType(fileTypeFromUrl);


						const blob = new Blob([response.data], { type: mimeType });
						const url = URL.createObjectURL(blob);

						setImageUrl(url);


						return () => URL.revokeObjectURL(url);
					} catch (error) {
						console.error('Error loading file:', error);
					}
				};

				fetchFile();
			}
		}
	}, [transaction]);



	useEffect(() => {
		transaction?.userId && refetch()
	}, [transaction])

	const fullName = user ? `${user.firstName || ''} ${user.lastName || ''}` : ''

	const { hasWriteAccess, onModalClose, onOptionClicked, modal, getTransactionOptions } =
		useBankTransactions()

	return (
		<DetailsLayout isFetching={isFetching} isError={isError} type={transaction?.status}>
			<LeftHeadDecoration left={'10px'}>
				<DText main>{params.detailId}</DText>
			</LeftHeadDecoration>

			{/* content */}
			<Flex fw justify='flex-end'>
				<Operation
					options={getTransactionOptions(transaction, transaction?.type === 'manual')}
					hasWriteAccess={hasWriteAccess}
					onOptionClicked={(idx) =>
						onOptionClicked(idx, transaction, transaction?.type === 'manual')
					}
				/>
			</Flex>

			<DetailRow>
				{transaction?.type === 'manual' ? (
					<>
						<DText main>
							<Text tid={'currency'} />
						</DText>
						<Flex justify={'flex-end'}>
							<CoinRow
								coin={{
									id: transaction?.currency,
									name: transaction?.currency,
									fa: transaction?.currency,
								}}
							/>
						</Flex>
					</>
				) : (
					<>
						<DText main>
							<Text tid={'bank-id'} />
						</DText>
						<Flex justify={'flex-end'}>
							<DText primary lineBreak={'anywhere'}>
								{transaction?.account}
							</DText>
						</Flex>
					</>
				)}
			</DetailRow>
			<DetailRow>
				<DText main>
					<Text tid={'fullname'} />
				</DText>
				<Flex justify={'flex-end'}>
					<DText primary lineBreak={'anywhere'}>
						{fullName}
					</DText>
				</Flex>
			</DetailRow>
			<DetailRow>
				<DText main>
					<Text tid={'transaction-flow'} />
				</DText>
				<Flex justify={'flex-end'}>
					<DText primary>
						<span className={'text-xs'}> ({transaction?.type}) </span>
						<Text tid={transaction?.flow} />
					</DText>
				</Flex>
			</DetailRow>
			<DetailRow>
				<DText main>
					<Text tid={'transaction-status'} />
				</DText>
				<Flex justify={'flex-end'}>
					<TradeTypeBadge color={getTradeTypeColor(transaction?.status)}>
						<Text tid={transaction?.status} />
					</TradeTypeBadge>
				</Flex>
			</DetailRow>

			{transaction?.txId && (
				<DetailRow>
					<DText main>
						<Text tid={'txid'} />
					</DText>
					<Flex justify={'flex-end'}>
						<TxidLink
							target='_blank'
							href={`${BASE_URL}coining/txid/${transaction?.txId}?network=${transaction?.network}&symbol=${transaction?.coin}`}
						>
							{transaction?.txId}
						</TxidLink>
					</Flex>
				</DetailRow>
			)}

			{transaction?.thirdParty && (
				<DetailRow>
					<DText main>
						<Text tid={'third-party'} />
					</DText>
					<Flex justify={'flex-end'}>
						<TradeTypeBadge color={getTradeTypeColor('limit')}>
							<Text tid={transaction?.thirdParty?.name} />
						</TradeTypeBadge>
					</Flex>
				</DetailRow>
			)}

			<DetailRow>
				<DText main>
					<Text tid={'amount'} />
				</DText>
				<Flex justify={'flex-end'}>
					<DText primary>{formatNumber(transaction?.amount)}</DText>
				</Flex>
			</DetailRow>
			<DetailRow>
				<DText main>
					<Text tid={`pure-${transaction?.flow}`} />
				</DText>
				<Flex justify={'flex-end'}>
					<DText primary>{formatNumber(transaction?.totalAmount)}</DText>
				</Flex>
			</DetailRow>
			{transaction?.type === 'manual' ? (
				<>
					{transaction?.fields?.map((f) => {
						return (
							<DetailRow key={f.id}>
								<DText main>
									<Text tid={f.name} />
								</DText>
								<Flex justify={'flex-end'}>
									{isValidURL(f.value) ? (
										<>
											{imageUrl ? (
												fileType === 'image' ? (
													<img
														src={imageUrl}
														alt=''
														width={100}
														height={100}
														style={{ cursor: 'zoom-in', objectFit: 'cover' }}
														onClick={toggleFullScreen}
													/>
												) : fileType === 'pdf' ? (
													<a
														href={imageUrl}
														download
														style={{
															color: '#007BFF',
															textDecoration: 'underline',
															cursor: 'pointer',
														}}
													>
														Download PDF
													</a>
												) : (
													<BiLoader color='#FFc800' className='animate-spin' />
												)
											) : (
												<BiLoader color='#FFc800' className='animate-spin' />
											)}
										</>
									) : (
										<DText primary lineBreak={'anywhere'}>
											{typeof f.value !== 'object' ? f.value : f.value.name || f.value.cardNo}
										</DText>
									)}
								</Flex>
							</DetailRow>
						)
					})}
				</>
			) : (
				<>
					<DetailRow>
						<DText main>
							<Text tid={'card-number'} />
						</DText>
						<Flex justify={'flex-end'}>
							<DText primary lineBreak={'anywhere'}>
								{transaction?.userAccount[0]?.cardNo}
							</DText>
						</Flex>
					</DetailRow>
					<DetailRow>
						<DText main>
							<Text tid={'sheba-number'} />
						</DText>
						<Flex justify={'flex-end'}>
							<DText primary lineBreak={'anywhere'}>
								{transaction?.userAccount[0]?.shebaNo}
							</DText>
						</Flex>
					</DetailRow>
				</>
			)}

			{transaction?.note && transaction.flow === 'withdraw' && (
				<DetailRow>
					<DText main>
						<Text tid={'note'} />
					</DText>
					<DText main style={{ textAlign: 'end' }}>
						{transaction?.note}
					</DText>
				</DetailRow>
			)}
			<Flex fw justify={'space-between'} style={{ marginTop: '20px' }}>
				<DText main>{formatDate(transaction?.createdAt, 'date', lang)}</DText>
				<DText main>{formatDate(transaction?.createdAt, 'time', lang)}</DText>
			</Flex>
			<ModalLayout open={modal.open} width={'520px'} onClose={onModalClose}>
				<TransactionModal details={modal} onClose={onModalClose} />
			</ModalLayout>

			{isImageFullScreen && (
				<div
					onClick={toggleFullScreen}
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100vw',
						height: '100vh',
						backgroundColor: 'rgba(0,0,0,0.8)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						zIndex: 1000,
					}}
				>
					<img
						src={imageUrl}
						alt=''
						style={{
							maxWidth: '85%',
							maxHeight: '85%',
							objectFit: 'contain',
							cursor: 'zoom-out',
						}}
					/>
				</div>
			)}

		</DetailsLayout>
	)
}

export default BankTransactionDetails
